// Module 4 / Slice 5b — EmploymentRecordsTab.
//
// Reads workforce.employment_record rows for the active employee.
// Write actions deferred to a Phase-4 follow-up (drawer create / edit /
// delete). PATTERNS.md §2.2.

import { useQuery } from "@tanstack/react-query";
import { workforceApiClient } from "../../../api/client";
import { useI18n } from "../../../i18n/I18nContext";

interface EmploymentRecordsTabProps {
  employeeId: string;
}

export function EmploymentRecordsTab({
  employeeId,
}: EmploymentRecordsTabProps): JSX.Element {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ["workforce", "employee", employeeId, "employment-records"] as const,
    queryFn: async () => {
      const response = await workforceApiClient.GET(
        "/workforce/employees/{employeeId}/employment-records",
        {
          params: {
            path: { employeeId },
            query: { limit: 200 },
          },
        },
      );
      if (!response.data) {
        throw new Error("workforce_employment_records_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div data-testid="employment-records-tab">
      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length === 0 && (
        <p data-testid="employment-records-empty">
          {t("workforce.employmentRecords.empty")}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length > 0 && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("workforce.employmentRecords.column.kind")}</th>
              <th>{t("workforce.employmentRecords.column.position")}</th>
              <th>{t("workforce.employmentRecords.column.employmentType")}</th>
              <th>{t("workforce.employmentRecords.column.startDate")}</th>
              <th>{t("workforce.employmentRecords.column.endDate")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((rec) => (
              <tr
                key={rec.id}
                data-testid={`employment-record-row-${rec.id}`}
              >
                <td>
                  <code>{rec.kind}</code>
                </td>
                <td>
                  {rec.positionKey ? <code>{rec.positionKey}</code> : "\u2014"}
                </td>
                <td>{rec.employmentType ?? "\u2014"}</td>
                <td>{rec.startDate}</td>
                <td>{rec.endDate ?? "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
